import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Target, CheckSquare, FolderKanban, ClipboardCheck, BookOpen,
  TrendingUp, Megaphone, Code2, Palette, BarChart3, LineChart,
  FileText, Bell, MessageSquare, History, Image as ImageIcon,
  ArrowRight, Lock, ShieldCheck, GitBranch, Database,
  Fingerprint, ScrollText, Activity, Users2, Rocket, Sparkles,
  ArrowUpRight, MoreHorizontal, Search, Circle, Check,
} from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

/* =====================================================================
 * DESIGN TOKENS  — single cohesive system used across every section.
 * ===================================================================== */

const FONT = { fontFamily: "'Inter', system-ui, -apple-system, sans-serif" };
const SERIF = { fontFamily: "'Instrument Serif', 'Playfair Display', Georgia, serif" };

// Homepage-scoped accent palette — matches the internal app's residual cyan
// accents (Task Table chevrons, Add Task gradient, Post filters) so the
// landing page feels like the same product. Kept as constants (not new CSS
// vars) so the global brand identity is untouched.
const ACCENT = {
  gradient: 'linear-gradient(135deg,#2dd4d4 0%,#06b6d4 50%,#0891b2 100%)',
  primary: '#06b6d4',
  primaryDark: '#0891b2',
  light: '#2dd4d4',
};

// Reusable className tokens
const T = {
  container: 'mx-auto max-w-[1200px] px-6 lg:px-10',
  section: 'py-24 lg:py-40',
  eyebrow: 'text-[13px] uppercase tracking-[0.14em] text-slate-500 font-medium',
  h1: 'text-[44px] sm:text-6xl lg:text-[80px] font-normal text-[#0b1620] leading-[1.02] tracking-[-0.02em]',
  h2: 'text-[36px] lg:text-[52px] font-normal text-[#0b1620] leading-[1.05] tracking-[-0.02em]',
  h3: 'text-[22px] lg:text-[24px] font-semibold text-[#0b1620] leading-[1.25] tracking-[-0.01em]',
  body: 'text-[19px] font-normal text-slate-600 leading-[1.65]',
  small: 'text-[15px] text-slate-600 leading-[1.6]',
  cardShadow: 'shadow-[0_1px_0_rgba(11,22,32,0.02),0_24px_60px_-30px_rgba(11,22,32,0.18)]',
  card: 'rounded-2xl border border-[rgba(11,22,32,0.07)] bg-white',
  btnPrimary:
    'inline-flex items-center gap-2 h-[52px] px-7 rounded-[10px] text-white text-[15px] font-medium ' +
    'shadow-[0_14px_30px_-10px_rgba(6,182,212,0.55)] ' +
    'hover:-translate-y-0.5 hover:shadow-[0_18px_36px_-10px_rgba(6,182,212,0.7)] transition-all duration-300',
  btnSecondary:
    'inline-flex items-center gap-2 h-[52px] px-7 rounded-[10px] bg-white text-[#0b1620] text-[15px] font-medium ' +
    'border border-[rgba(11,22,32,0.12)] hover:border-[rgba(11,22,32,0.2)] hover:bg-[#f7fbfc] transition-all duration-300',
};

/* =====================================================================
 * SMALL PRIMITIVES
 * ===================================================================== */

const Section = ({ id, eyebrow, children, className = '' }) => (
  <section id={id} className={`${T.section} ${className}`}>
    <div className={T.container}>
      {eyebrow && <p className={`${T.eyebrow} mb-6`}>{eyebrow}</p>}
      {children}
    </div>
  </section>
);

/* Fade-up on-scroll utility — CSS-only via IntersectionObserver */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll('[data-reveal]');
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-revealed');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* =====================================================================
 * DASHBOARD MOCKUP — realistic enterprise UI (neutral labels, no fake identities).
 * ===================================================================== */

const KPI = ({ label, value, trend, positive = true }) => (
  <div className="rounded-xl border border-slate-200/80 bg-white p-4 lg:p-5">
    <p className="text-[12px] text-slate-500 font-medium">{label}</p>
    <div className="mt-2 flex items-baseline gap-2">
      <span className="text-[22px] font-semibold text-brand-navy tracking-tight">{value}</span>
      {trend && (
                <span className={`text-[11px] font-medium ${positive ? 'text-[#06b6d4]' : 'text-slate-500'}`}>
          {positive ? '↑' : '↓'} {trend}
        </span>
      )}
    </div>
  </div>
);

const StatusBadge = ({ tone, children }) => {
  // Cyan-family tones matching the reference HTML (`rgba(6,182,212,…)`, `rgba(14,116,144,…)`, `rgba(45,212,212,…)`).
  const tones = {
    review:    'bg-[rgba(6,182,212,0.12)]  text-[#0891b2] border-[rgba(6,182,212,0.2)]',
    approval:  'bg-[rgba(8,145,178,0.14)]  text-[#0c5a7a] border-[rgba(8,145,178,0.22)]',
    executing: 'bg-[rgba(14,116,144,0.14)] text-[#0e7490] border-[rgba(14,116,144,0.22)]',
    planning:  'bg-[rgba(45,212,212,0.18)] text-[#0891b2] border-[rgba(45,212,212,0.28)]',
    done:      'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-[5px] text-[11px] font-medium border ${tones[tone] || tones.planning}`}>
      <span className="h-1 w-1 rounded-full bg-current opacity-70" />
      {children}
    </span>
  );
};

const ProgressBar = ({ value }) => (
  <div className="h-1.5 rounded-full bg-[rgba(11,22,32,0.06)] overflow-hidden">
    <div
      className="h-full rounded-full"
      style={{ width: `${value}%`, background: ACCENT.gradient }}
    />
  </div>
);

const Dashboard = () => (
  <div className="relative">
    {/* soft ambient glow */}
    <div className="absolute -inset-10 bg-gradient-to-br from-slate-100/40 via-blue-50/30 to-transparent blur-2xl -z-10 rounded-[32px]" />

    <div
      className={`relative ${T.card} ${T.cardShadow} overflow-hidden animate-[float_8s_ease-in-out_infinite]`}
      style={{ animationName: 'floatDash' }}
    >
      {/* chrome */}
      <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100">
        <span className="w-2.5 h-2.5 rounded-full bg-slate-200" />
        <span className="w-2.5 h-2.5 rounded-full bg-slate-200" />
        <span className="w-2.5 h-2.5 rounded-full bg-slate-200" />
        <div className="ml-4 flex items-center gap-2 h-6 px-3 rounded-md bg-slate-50 border border-slate-100">
          <Lock size={10} className="text-slate-400" />
          <span className="text-[11px] text-slate-500 font-medium">clarity.internal/workspace</span>
        </div>
      </div>

      {/* body */}
      <div className="grid grid-cols-[220px_1fr] min-h-[520px]">
        {/* sidebar */}
        <aside className="border-r border-slate-100 p-4 space-y-1 bg-slate-50/40">
          <div className="flex items-center gap-2 mb-5 px-2">
            <BrandLogo size={22} className="rounded-md" />
            <span className="text-[13px] font-semibold text-slate-900">Clarity</span>
          </div>
          {[
            { icon: BarChart3, label: 'Overview', active: true },
            { icon: Target, label: 'Initiatives' },
            { icon: CheckSquare, label: 'Tasks' },
            { icon: ClipboardCheck, label: 'Approvals', badge: 4 },
            { icon: Users2, label: 'Teams' },
            { icon: LineChart, label: 'Analytics' },
          ].map(({ icon: I, label, active, badge }) => (
            <div
              key={label}
              className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] ${
                active ? 'bg-white text-brand-navy font-medium shadow-sm border border-slate-200/60' : 'text-slate-600'
              }`}
            >
              <I size={14} strokeWidth={active ? 2.2 : 1.8} />
              <span className="flex-1">{label}</span>
              {badge && (
                <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 rounded-full px-1.5 py-0.5">{badge}</span>
              )}
            </div>
          ))}
        </aside>

        {/* main */}
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] text-slate-500 uppercase tracking-wider font-medium">Q3 · Operational overview</p>
              <h4 className="mt-1 text-[20px] font-semibold text-brand-navy tracking-tight">Good morning, Team</h4>
            </div>
            <div className="flex items-center gap-2">
              <div className="hidden sm:flex items-center gap-2 h-9 w-56 px-3 rounded-lg border border-slate-200 bg-white text-[12px] text-slate-400">
                <Search size={12} /> Search initiatives…
              </div>
              <button
                className="h-9 px-3.5 rounded-lg text-white text-[12px] font-medium"
                style={{ background: ACCENT.gradient, boxShadow: '0 6px 18px -6px rgba(6,182,212,0.55)' }}
              >
                + New initiative
              </button>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <KPI label="Active initiatives" value="28" trend="4 this week" />
            <KPI label="Pending approvals" value="7" trend="3 awaiting you" positive={false} />
            <KPI label="On track" value="82%" trend="+3% MoM" />
            <KPI label="Cycle time" value="6.4d" trend="1.2d faster" />
          </div>

          <div className="grid grid-cols-[1.6fr_1fr] gap-4">
            {/* table */}
            <div className="rounded-xl border border-slate-200/80 overflow-hidden bg-white">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
                <span className="text-[12px] font-semibold text-slate-700">Active initiatives</span>
                <MoreHorizontal size={14} className="text-slate-400" />
              </div>
              <table className="w-full">
                <thead>
                  <tr className="text-left">
                    {['Initiative', 'Stage', 'Progress', 'Due'].map((h) => (
                      <th key={h} className="px-4 py-2 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name: 'FY26 brand refresh rollout', stage: 'review', progress: 74, due: 'Oct 12' },
                    { name: 'Internal API consolidation', stage: 'executing', progress: 52, due: 'Nov 03' },
                    { name: 'SEO content engine — Q4 plan', stage: 'approval', progress: 91, due: 'Sep 28' },
                    { name: 'Leadership OKR cascade — H2', stage: 'planning', progress: 28, due: 'Dec 15' },
                  ].map((r) => (
                    <tr key={r.name} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-[12.5px] font-medium text-slate-800">{r.name}</td>
                      <td className="px-4 py-3">
                        <StatusBadge tone={r.stage}>
                          {r.stage[0].toUpperCase() + r.stage.slice(1)}
                        </StatusBadge>
                      </td>
                      <td className="px-4 py-3 w-[140px]">
                        <ProgressBar value={r.progress} />
                        <span className="text-[10px] text-slate-500 mt-1 inline-block">{r.progress}%</span>
                      </td>
                      <td className="px-4 py-3 text-[12px] text-slate-600">{r.due}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* activity */}
            <div className="rounded-xl border border-slate-200/80 bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[12px] font-semibold text-slate-700">Team activity</span>
                <span className="text-[10px] font-medium" style={{ color: ACCENT.primary }}>● Live</span>
              </div>
              <div className="space-y-3">
                {[
                  { i: 'MC', c: 'from-blue-500 to-blue-600', a: 'approved', o: 'Brand refresh · Phase 2', t: '2m' },
                  { i: 'RO', c: 'from-violet-500 to-violet-600', a: 'commented on', o: 'API consolidation', t: '18m' },
                  { i: 'LP', c: 'from-amber-500 to-amber-600', a: 'moved', o: 'Q4 content plan → Approval', t: '1h' },
                  { i: 'SI', c: 'from-emerald-500 to-emerald-600', a: 'completed', o: 'H2 OKR draft', t: '3h' },
                ].map((e, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className={`h-7 w-7 rounded-full bg-gradient-to-br ${e.c} text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0`}>
                      {e.i}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] text-slate-800 leading-snug">
                        <span className="text-slate-500">{e.a}</span>{' '}
                        <span className="font-medium">{e.o}</span>
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{e.t} ago</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

/* =====================================================================
 * DATA
 * ===================================================================== */

const MODULES = [
  { n: '01', icon: Target, name: 'Strategy Planning', desc: 'Define initiatives, OKRs, and cascading goals.' },
  { n: '02', icon: CheckSquare, name: 'Task Management', desc: 'Assign, prioritize, and track work to completion.' },
  { n: '03', icon: FolderKanban, name: 'Project Tracking', desc: 'Milestones, dependencies, and live status.' },
  { n: '04', icon: ClipboardCheck, name: 'Manager Approvals', desc: 'Structured review with full decision history.' },
  { n: '05', icon: BookOpen, name: 'Knowledge Hub', desc: 'Documentation, runbooks, and team know-how.' },
  { n: '06', icon: TrendingUp, name: 'SEO Workspace', desc: 'Briefs, rankings, and content operations.' },
  { n: '07', icon: Megaphone, name: 'Marketing Workspace', desc: 'Campaign planning, calendars, and execution.' },
  { n: '08', icon: Code2, name: 'Development', desc: 'Backlogs, sprints, releases, and bugs.' },
  { n: '09', icon: Palette, name: 'UI / UX Workspace', desc: 'Design reviews, assets, and versioning.' },
  { n: '10', icon: BarChart3, name: 'Reports', desc: 'Recurring and ad-hoc operational reporting.' },
  { n: '11', icon: LineChart, name: 'Analytics', desc: 'Cross-module metrics and performance views.' },
  { n: '12', icon: FileText, name: 'Document Management', desc: 'Versioned files with structured access.' },
  { n: '13', icon: Bell, name: 'Notifications', desc: 'Context-aware alerts routed by role.' },
  { n: '14', icon: MessageSquare, name: 'Comments', desc: 'Threaded discussion attached to any artifact.' },
  { n: '15', icon: History, name: 'Timeline', desc: 'A unified history of every change and decision.' },
  { n: '16', icon: ImageIcon, name: 'Assets', desc: 'Brand, media, and source-of-truth library.' },
];

const FLOW = [
  { n: '01', name: 'Plan', desc: 'Define intent and scope.' },
  { n: '02', name: 'Create', desc: 'Draft work items.' },
  { n: '03', name: 'Assign', desc: 'Owners and stakeholders.' },
  { n: '04', name: 'Collaborate', desc: 'Work together in context.' },
  { n: '05', name: 'Review', desc: 'Structured feedback.' },
  { n: '06', name: 'Approve', desc: 'Recorded decisions.' },
  { n: '07', name: 'Execute', desc: 'Deliver and ship.' },
  { n: '08', name: 'Measure', desc: 'Outcomes and signals.' },
  { n: '09', name: 'Improve', desc: 'Iterate on the system.' },
];

const ROLES = [
  { title: 'Manager', tagline: 'Decide and unblock.', items: ['Approvals queue', 'Reports', 'KPIs', 'Team monitoring', 'Cross-team views'] },
  { title: 'Developer', tagline: 'Build and ship.', items: ['Tasks & sprints', 'Bugs', 'Deployments', 'Code reviews', 'Timeline'] },
  { title: 'Designer', tagline: 'Craft and review.', items: ['UI review', 'Assets', 'Feedback', 'Versioning', 'Handoffs'] },
  { title: 'Marketing', tagline: 'Plan and launch.', items: ['Campaigns', 'SEO', 'Content', 'Analytics', 'Planning'] },
  { title: 'Leadership', tagline: 'See the whole picture.', items: ['Dashboards', 'Performance', 'Approvals', 'Roadmaps', 'Insights'] },
];

const SECURITY = [
  { icon: Lock, title: 'Role permissions', desc: 'Granular access tied to function, team, and seniority.' },
  { icon: ScrollText, title: 'Audit logs', desc: 'Every action recorded with actor, time, and context.' },
  { icon: ClipboardCheck, title: 'Approval history', desc: 'A traceable record of every decision made.' },
  { icon: Activity, title: 'Activity tracking', desc: 'Operational signals across modules and teams.' },
  { icon: Fingerprint, title: 'Secure authentication', desc: 'Token-backed sign-in with session controls.' },
  { icon: Database, title: 'Data integrity', desc: 'Versioned, validated, recoverable system of record.' },
];

const VISION = [
  { stage: 'NOW', status: 'Live', title: 'Current platform', desc: 'Strategy, execution, approvals, and analytics—running today.' },
  { stage: 'NEXT', status: 'In build', title: 'Advanced automation', desc: 'Rule-based workflows, scheduled actions, smart routing.' },
  { stage: '2026', status: 'Planned', title: 'AI assistance', desc: 'Summaries, drafting, and contextual recommendations.' },
  { stage: '2027', status: 'Planned', title: 'Enterprise intelligence', desc: 'Cross-functional insights from unified operational data.' },
  { stage: 'Beyond', status: 'Vision', title: 'Predictive operations', desc: 'Forecasting outcomes, capacity, and risk in real time.' },
];

const CONNECTED_LABELS = ['Leadership', 'Marketing', 'SEO', 'Development', 'Design', 'Operations', 'Management'];

/* =====================================================================
 * NAV
 * ===================================================================== */

const NavBar = ({ user, onAccess }) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  return (
    <nav
      data-testid="homepage-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/85 backdrop-blur-xl border-b border-[rgba(11,22,32,0.07)]'
          : 'bg-transparent'
      }`}
    >
      <div className={`${T.container} h-[72px] flex items-center justify-between`}>
        <Link to="/" className="flex items-center gap-2.5" data-testid="homepage-logo">
          <BrandLogo size={32} className="rounded-[9px] shadow-[0_6px_18px_-6px_rgba(6,182,212,0.55)]" />
          <span className="text-[15px] font-semibold text-[#0b1620] tracking-tight">Clarity</span>
          <span className="hidden sm:inline text-[13px] text-slate-400 font-normal">Strategy Manager</span>
          <span className="hidden md:inline-flex items-center h-[22px] px-2 ml-1 rounded-[5px] border border-[rgba(11,22,32,0.12)] bg-[#f7fbfc] text-[10px] font-semibold text-[#5a6a78] uppercase tracking-wider">
            Internal
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-9 text-[14px] text-[#3a4a5a]">
          {['Platform', 'Modules', 'Workflow', 'Security', 'Roadmap'].map((l) => (
            <a
              key={l}
              href={`#${l.toLowerCase()}`}
              className="hover:text-[#0b1620] transition-colors duration-200 font-medium"
            >
              {l}
            </a>
          ))}
        </div>
        <button
          onClick={onAccess}
          data-testid="nav-access-btn"
          className="h-10 px-5 rounded-lg text-white text-[13.5px] font-medium hover:-translate-y-0.5 transition-all duration-300"
          style={{ background: ACCENT.gradient, boxShadow: '0 6px 18px -6px rgba(6,182,212,0.6)' }}
        >
          {user ? 'Workspace' : 'Login'}
        </button>
      </div>
    </nav>
  );
};

/* =====================================================================
 * RADIAL DIAGRAM
 * ===================================================================== */

const ConnectedDiagram = () => (
  <div className="relative mx-auto w-full max-w-[500px] aspect-square">
    <div className="absolute inset-0 rounded-full border border-dashed border-slate-200" />
    <div className="absolute inset-[14%] rounded-full border border-dashed border-slate-200" />
    <div className="absolute inset-[28%] rounded-full border border-dashed border-slate-100" />
    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-blue-50/40 via-transparent to-slate-50/40 -z-10" />

    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
      {CONNECTED_LABELS.map((_, i) => {
        const a = ((i * 360) / CONNECTED_LABELS.length - 90) * (Math.PI / 180);
        const r = 42;
        return <line key={i} x1={50} y1={50} x2={50 + r * Math.cos(a)} y2={50 + r * Math.sin(a)} stroke="rgb(226 232 240)" strokeWidth="0.2" strokeDasharray="0.6 0.6" />;
      })}
    </svg>

    <div className="absolute inset-0 flex items-center justify-center">
      <div className="rounded-2xl border border-slate-900 bg-brand-navy text-white px-5 py-3.5 text-[13px] font-medium shadow-2xl shadow-slate-900/20 z-10">
        <div className="flex items-center gap-2">
          <BrandLogo size={18} className="rounded-md" />
          Clarity Platform
        </div>
      </div>
    </div>

    {CONNECTED_LABELS.map((label, i) => {
      const angleDeg = (i * 360) / CONNECTED_LABELS.length - 90;
      const angleRad = (angleDeg * Math.PI) / 180;
      const r = 42;
      const x = 50 + r * Math.cos(angleRad);
      const y = 50 + r * Math.sin(angleRad);
      return (
        <div
          key={label}
          data-testid={`connected-label-${label.toLowerCase()}`}
          className="absolute z-20"
          style={{ left: `${x}%`, top: `${y}%`, transform: 'translate(-50%, -50%)' }}
        >
          <div className="rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-[12px] font-medium text-slate-700 shadow-[0_2px_10px_-4px_rgba(15,23,42,0.12)] whitespace-nowrap">
            {label}
          </div>
        </div>
      );
    })}
  </div>
);

/* =====================================================================
 * HOMEPAGE
 * ===================================================================== */

const Homepage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  useReveal();

  const goToWorkspace = () => {
    if (user) {
      const map = {
        Manager: '/dashboard', Admin: '/dashboard', 'Team Leader': '/dashboard', 'Team Member': '/my-day',
        Developer: '/my-day', 'UI/UX Designer': '/my-day', 'Creative Head': '/social-media', 'Read-only': '/kanban',
      };
      navigate(map[user.role_level] || '/dashboard');
    } else navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white text-brand-navy antialiased" style={FONT} data-testid="homepage">
      {/* global scoped styles */}
      <style>{`
        [data-reveal]{opacity:0;transform:translateY(16px);transition:opacity 700ms cubic-bezier(0.16,1,0.3,1),transform 700ms cubic-bezier(0.16,1,0.3,1);}
        [data-reveal].is-revealed{opacity:1;transform:translateY(0);}
        @keyframes floatDash {0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        .float-dash{animation:floatDash 8s ease-in-out infinite;}
      `}</style>

      <NavBar user={user} onAccess={goToWorkspace} />

      {/* ============================================================ HERO */}
      <header id="platform" className="relative pt-40 lg:pt-48 pb-16 lg:pb-24 overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute top-24 -left-24 h-[420px] w-[420px] bg-blue-100/40 rounded-full blur-3xl" />
          <div className="absolute top-64 -right-24 h-[420px] w-[420px] bg-slate-100/60 rounded-full blur-3xl" />
        </div>

        <div className={T.container}>
          <div className="max-w-[720px]" data-reveal>
            <div className="inline-flex items-center gap-2 h-8 px-3 rounded-full border border-slate-200 bg-white/70 backdrop-blur text-[12px] text-slate-600 font-medium mb-8">
              <span className="h-1.5 w-1.5 rounded-full bg-[#06b6d4] shadow-[0_0_8px_rgba(6,182,212,0.6)]" />
              Internal operating platform · v4.2
            </div>
            <h1 className={T.h1} style={{ ...SERIF, maxWidth: '640px' }}>
              One platform.<br />
              <span className="text-slate-500">Complete strategic execution.</span>
            </h1>
            <p className={`mt-8 ${T.body}`} style={{ maxWidth: '580px' }}>
              Clarity Strategy Manager brings strategy, execution, collaboration, approvals, and performance into a single connected workspace—so every team moves with alignment, accountability, and clarity.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <button
                onClick={goToWorkspace}
                data-testid="hero-access-btn"
                className={T.btnPrimary}
                style={{ background: ACCENT.gradient }}
              >
                Access workspace
                <ArrowRight size={16} />
              </button>
              <a href="#platform-explore" data-testid="hero-explore-btn" className={T.btnSecondary}>
                Explore platform
              </a>
            </div>
          </div>

          {/* Dashboard mockup — 100px gap */}
          <div className="mt-24 lg:mt-32" data-reveal>
            <Dashboard />
          </div>
        </div>
      </header>

      {/* ================================================ TRUST STRIP */}
      <section className="border-y border-slate-100 py-10">
        <div className={T.container}>
          <div className="flex flex-wrap items-center justify-between gap-8" data-reveal>
            <p className="text-[13px] uppercase tracking-[0.14em] text-slate-500 font-medium">
              Purpose-built for enterprise operations
            </p>
            <div className="flex flex-wrap items-center gap-x-10 gap-y-3 text-[13px] text-slate-600">
              {['Strategy', 'Execution', 'Approvals', 'Analytics', 'Governance'].map((k) => (
                <span key={k} className="inline-flex items-center gap-2">
                  <Check size={13} className="text-[#06b6d4]" strokeWidth={2.4} />
                  {k}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================================================ WHY */}
      <Section id="platform-explore" eyebrow="01 — Why Clarity exists">
        <div data-reveal>
          <h2 className={T.h2} style={{ maxWidth: '820px' }}>
            Work should be connected,<br />not fragmented.
          </h2>
          <div className="mt-12 grid lg:grid-cols-2 gap-x-16 gap-y-6" style={{ maxWidth: '900px' }}>
            <p className={T.body}>
              Modern organizations rely on a dozen disconnected tools for planning, communication, approvals, reporting, and execution. The result is duplicated work, lost context, and reduced visibility.
            </p>
            <p className={T.body}>
              Clarity centralizes every stage of execution into a unified operational platform—so teams collaborate efficiently while leadership maintains complete visibility across initiatives.
            </p>
          </div>
        </div>

        <div className="mt-20 grid md:grid-cols-3 gap-4">
          {[
            { icon: Target, title: 'Strategic alignment', desc: 'Every initiative stays connected to organizational goals—visible from team to leadership.' },
            { icon: Activity, title: 'Operational visibility', desc: 'Track work, progress, approvals, ownership, and execution in a single source of truth.' },
            { icon: Users2, title: 'Team collaboration', desc: 'Structured communication across departments—decisions, context, and threads stay together.' },
          ].map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              data-reveal
              className={`${T.card} p-8 hover:border-slate-300 hover:-translate-y-1 hover:${T.cardShadow.split(' ')[0]} transition-all duration-300`}
            >
              <div className="h-11 w-11 rounded-xl bg-brand-navy text-white flex items-center justify-center mb-6">
                <Icon size={18} strokeWidth={1.8} />
              </div>
              <h3 className={T.h3}>{title}</h3>
              <p className={`mt-3 ${T.small}`}>{desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ================================================ MODULES */}
      <Section id="modules" eyebrow="02 — Core modules" className="border-t border-slate-100">
        <div className="flex flex-wrap items-end justify-between gap-10 mb-16" data-reveal>
          <h2 className={T.h2} style={{ maxWidth: '640px' }}>
            Sixteen modules.<br />One coherent system.
          </h2>
          <p className={T.body} style={{ maxWidth: '440px' }}>
            Each module is purpose-built for a function and shares the same data model—so context travels with the work.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {MODULES.map(({ n, icon: Icon, name, desc }) => (
            <div
              key={n}
              data-reveal
              data-testid={`module-card-${n}`}
              className={`${T.card} p-6 hover:border-slate-900 hover:${T.cardShadow.split(' ')[0]} hover:-translate-y-0.5 transition-all duration-300 group cursor-default`}
            >
              <div className="flex items-start justify-between mb-10">
                <Icon size={20} strokeWidth={1.6} className="text-slate-700 group-hover:text-brand-navy transition-colors" />
                <span className="text-[11px] text-slate-400 font-mono tracking-wider">{n}</span>
              </div>
              <h3 className="text-[15px] font-semibold text-brand-navy mb-1.5">{name}</h3>
              <p className="text-[12.5px] text-slate-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ================================================ WORKFLOW */}
      <Section id="workflow" eyebrow="03 — How work flows" className="bg-slate-50/50 border-y border-slate-100">
        <div data-reveal>
          <h2 className={T.h2} style={{ maxWidth: '780px' }}>
            From intent to outcome—<br />one continuous flow.
          </h2>
          <p className={`mt-8 ${T.body}`} style={{ maxWidth: '580px' }}>
            Each stage hands off cleanly to the next. Context, ownership, and history travel with the work.
          </p>
        </div>
        <div className="mt-16 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-9 gap-2">
          {FLOW.map((s) => (
            <div
              key={s.n}
              data-reveal
              className={`${T.card} p-4 hover:border-slate-900 hover:-translate-y-0.5 transition-all duration-300`}
              data-testid={`flow-step-${s.name.toLowerCase()}`}
            >
              <p className="text-[10px] font-mono text-slate-400 tracking-wider mb-3">{s.n}</p>
              <p className="text-[13px] font-semibold text-brand-navy">{s.name}</p>
              <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">{s.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ================================================ CONNECTED */}
      <Section eyebrow="04 — Everything connected">
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-16 items-center">
          <div data-reveal>
            <h2 className={T.h2} style={{ maxWidth: '540px' }}>
              Every department.<br />One connected workflow.
            </h2>
            <p className={`mt-8 ${T.body}`} style={{ maxWidth: '520px' }}>
              Everyone works inside the same ecosystem while maintaining role-specific responsibilities, views, and permissions. Cross-functional handoffs become a continuation—not a translation.
            </p>
            <div className="mt-10 grid sm:grid-cols-2 gap-3" style={{ maxWidth: '560px' }}>
              {[
                { title: 'Shared data model', desc: 'One object per initiative, task, or decision.' },
                { title: 'Role-aware views', desc: 'Each team sees what\u2019s relevant to their function.' },
                { title: 'Bidirectional handoffs', desc: 'Comments, approvals, and edits flow in context.' },
                { title: 'Single source of record', desc: 'Every artifact lives where the work lives.' },
              ].map(({ title, desc }) => (
                <div key={title} className={`${T.card} p-5 hover:border-slate-300 transition-colors`}>
                  <h3 className="font-semibold text-brand-navy text-[14px] mb-1">{title}</h3>
                  <p className="text-[12.5px] text-slate-500 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div data-reveal><ConnectedDiagram /></div>
        </div>
      </Section>

      {/* ================================================ ROLE */}
      <Section eyebrow="05 — Role-based workspace" className="bg-slate-50/50 border-y border-slate-100">
        <h2 className={T.h2} style={{ maxWidth: '620px' }} data-reveal>
          A workspace shaped<br />by your role.
        </h2>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {ROLES.map((r) => (
            <div
              key={r.title}
              data-reveal
              data-testid={`role-card-${r.title.toLowerCase()}`}
              className={`${T.card} p-6 hover:border-slate-900 hover:-translate-y-0.5 transition-all duration-300`}
            >
              <h3 className="text-[15px] font-semibold text-brand-navy">{r.title}</h3>
              <p className="text-[12px] text-slate-500 mt-1 mb-5">{r.tagline}</p>
              <ul className="space-y-2">
                {r.items.map((it) => (
                  <li key={it} className="flex items-center gap-2 text-[13px] text-slate-700">
                    <Circle size={4} className="text-slate-400 fill-current" />
                    {it}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* ================================================ ANALYTICS */}
      <Section eyebrow="06 — Analytics & visibility">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-20 items-center">
          <div data-reveal>
            <h2 className={T.h2} style={{ maxWidth: '520px' }}>
              Complete visibility, without complexity.
            </h2>
            <p className={`mt-8 ${T.body}`} style={{ maxWidth: '480px' }}>
              Track project progress, workload, approvals, timelines, and execution from a centralized dashboard designed to support informed decision-making across every level of the organization.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-2" style={{ maxWidth: '440px' }}>
              {['Project Health', 'Team Progress', 'Approval Status', 'Task Distribution', 'Timeline Tracking', 'Performance Overview'].map((k) => (
                <div key={k} className="flex items-center gap-2 text-[14px] text-slate-700 py-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-navy" />
                  {k}
                </div>
              ))}
            </div>
          </div>
          <div data-reveal className={`${T.card} ${T.cardShadow} p-6 lg:p-8`}>
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">Initiative throughput</p>
            <p className="mt-1 text-[13px] text-slate-400">Last 12 weeks</p>
            <div className="mt-6 flex items-end gap-1.5 h-32">
              {[42, 58, 66, 71, 63, 78, 84, 76, 88, 92, 85, 96].map((h, i) => (
                <div key={i} className="flex-1 flex flex-col gap-0.5 justify-end">
                  <div className="rounded-t bg-[#06b6d4]" style={{ height: `${h * 0.55}%` }} />
                  <div className="rounded-t bg-[#DBE7F5]" style={{ height: `${(100 - h) * 0.28}%` }} />
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-4 text-[11px] text-slate-500">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#06b6d4]" />Completed</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-sm bg-[#DBE7F5]" />In review</span>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4 pt-6 border-t border-slate-100">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-3">Team progress</p>
                <div className="space-y-2.5">
                  {[{ t: 'Marketing', v: 82 }, { t: 'Engineering', v: 66 }, { t: 'Design', v: 91 }, { t: 'SEO', v: 55 }].map((r) => (
                    <div key={r.t}>
                      <div className="flex justify-between text-[11px] text-slate-600 mb-1"><span>{r.t}</span><span>{r.v}</span></div>
                      <ProgressBar value={r.v} />
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-medium mb-3">Approval queue</p>
                <div className="text-[36px] font-bold text-brand-navy tracking-tight leading-none">7</div>
                <p className="text-[12px] text-slate-500 mt-2">awaiting review · median 1.4 days</p>
                <div className="mt-4 space-y-1.5">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="h-6 rounded-md border border-slate-100 bg-slate-50/60" />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ================================================ SECURITY */}
      <Section id="security" className="bg-brand-navy border-y border-slate-900 text-slate-100">
        <p className={`text-[13px] uppercase tracking-[0.14em] text-slate-400 font-medium mb-6`} data-reveal>07 — Security &amp; governance</p>
        <h2
          className="text-[36px] lg:text-[52px] font-bold text-white leading-[1.05] tracking-[-0.03em]"
          style={{ maxWidth: '720px' }}
          data-reveal
        >
          Enterprise security, by design.
        </h2>
        <p
          className="mt-8 text-[19px] font-normal text-slate-400 leading-[1.65]"
          style={{ maxWidth: '580px' }}
          data-reveal
        >
          Built with role-based access control, structured permissions, audit history, and centralized governance—so operational information stays secure, traceable, and accessible only to authorized users.
        </p>
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {SECURITY.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={title}
              data-reveal
              data-testid={`security-card-${String(i + 1).padStart(2, '0')}`}
              className="rounded-2xl border border-slate-800 bg-slate-900/40 backdrop-blur-sm p-6 hover:border-slate-700 hover:bg-slate-900 hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="h-10 w-10 rounded-xl border border-slate-800 bg-brand-navy flex items-center justify-center mb-6">
                <Icon size={18} strokeWidth={1.6} className="text-slate-100" />
              </div>
              <h3 className="text-[15px] font-semibold text-white mb-1.5">{title}</h3>
              <p className="text-[12.5px] text-slate-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ================================================ BUILT FOR US */}
      <Section eyebrow="08 — Built for our teams">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div data-reveal>
            <h2 className={T.h2}>
              Not built for everyone.<br />
              <span className="text-slate-500">Built for us.</span>
            </h2>
            <p className={`mt-8 ${T.body}`} style={{ maxWidth: '480px' }}>
              Clarity is shaped by the way our organization actually plans, decides, and executes—calibrated by the teams who use it every day.
            </p>
          </div>
          <div className="space-y-3" data-reveal>
            {[
              { icon: Lock, label: 'Internal access only', sub: 'Provisioned for authorized team members.' },
              { icon: GitBranch, label: 'Configured to our workflows', sub: 'Tailored to how our departments coordinate.' },
              { icon: ShieldCheck, label: 'Owned and operated in-house', sub: 'Governed and evolved by our own teams.' },
            ].map(({ icon: Icon, label, sub }) => (
              <div key={label} className={`${T.card} p-5 hover:border-slate-900 transition-colors flex items-start gap-4`}>
                <div className="h-10 w-10 rounded-xl bg-brand-navy text-white flex items-center justify-center flex-shrink-0">
                  <Icon size={16} />
                </div>
                <div>
                  <p className="font-semibold text-brand-navy text-[14px]">{label}</p>
                  <p className="text-[12.5px] text-slate-500 mt-0.5">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ================================================ VISION */}
      <Section id="roadmap" eyebrow="09 — Future vision" className="bg-slate-50/50 border-y border-slate-100">
        <div data-reveal>
          <h2 className={T.h2} style={{ maxWidth: '640px' }}>
            Growing with our organization.
          </h2>
          <p className={`mt-8 ${T.body}`} style={{ maxWidth: '560px' }}>
            Clarity continues to evolve—supporting new operational requirements, emerging workflows, automation, AI-assisted productivity, and deeper cross-functional collaboration as we grow.
          </p>
        </div>
        <div className="mt-16 relative">
          <div className="absolute left-4 lg:left-1/2 top-2 bottom-2 w-px bg-slate-200 lg:-translate-x-1/2" />
          <div className="space-y-4">
            {VISION.map((v, i) => (
              <div
                key={v.stage}
                data-reveal
                data-testid={`vision-stage-${v.stage.toLowerCase()}`}
                className={`relative flex items-start ${i % 2 === 0 ? 'lg:flex-row-reverse' : ''} gap-6 lg:gap-10`}
              >
                <div className="hidden lg:block flex-1" />
                <div className="absolute left-4 lg:left-1/2 top-6 -translate-x-1/2 h-3 w-3 rounded-full bg-white border-2 border-[#06b6d4] z-10 shadow-[0_0_0_4px_rgba(6,182,212,0.15)]" />
                <div className="flex-1 pl-12 lg:pl-0">
                  <div className={`${T.card} p-6 hover:border-slate-900 transition-colors`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-[11px] font-mono tracking-wider text-brand-navy font-semibold">{v.stage}</span>
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">{v.status}</span>
                    </div>
                    <h3 className="font-semibold text-brand-navy text-[15px] mb-1.5 flex items-center gap-2">
                      {i === 0 && <Rocket size={14} />}
                      {i === 2 && <Sparkles size={14} />}
                      {v.title}
                    </h3>
                    <p className="text-[13px] text-slate-600 leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ================================================ FINAL CTA */}
      <section className="py-28 lg:py-40">
        <div className={`${T.container} text-center`}>
          <h2 className={`${T.h2} mx-auto`} style={{ maxWidth: '640px' }} data-reveal>
            Ready to continue your work?
          </h2>
          <p className={`mt-8 mx-auto ${T.body}`} style={{ maxWidth: '520px' }} data-reveal>
            Access the workspace to manage projects, collaborate with your team, and keep every initiative moving forward.
          </p>
          <div className="mt-12 flex justify-center gap-3" data-reveal>
            <button onClick={goToWorkspace} data-testid="final-cta-btn" className={T.btnPrimary}>
              Access workspace
              <ArrowUpRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* ================================================ FOOTER */}
      <footer className="border-t border-slate-100 pt-16 pb-10">
        <div className={T.container}>
          <div className="grid md:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <BrandLogo size={28} className="rounded-lg" />
                <span className="text-[15px] font-semibold text-brand-navy">Clarity</span>
              </div>
              <p className="text-[12.5px] text-slate-500 leading-relaxed">
                Clarity Strategy Manager<br />
                Internal operational platform.
              </p>
            </div>
            {[
              { title: 'Platform', items: ['Modules', 'Workflow', 'Roadmap', 'Documentation'] },
              { title: 'Resources', items: ['Release notes', 'Support', 'Guidelines', 'Help center'] },
              { title: 'Governance', items: ['Privacy', 'Terms', 'Security'] },
            ].map((c) => (
              <div key={c.title}>
                <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-3">{c.title}</p>
                <ul className="space-y-2">
                  {c.items.map((i) => (
                    <li key={i}><span className="text-[14px] text-slate-700 hover:text-brand-navy cursor-default transition-colors">{i}</span></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="pt-8 border-t border-slate-100 flex flex-wrap justify-between gap-4 text-[12.5px] text-slate-500">
            <span>© 2026 Clarity Strategy Manager</span>
            <span>Built exclusively for internal teams.</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
